import * as THREE from 'three';
import { TVScreen, ScreenConfig } from './TVScreen';
import { ContentOverlay } from '../ui/ContentOverlay';
import { languageManager } from '../i18n/LanguageManager';

export class ScreenManager {
    private screens: TVScreen[] = [];
    private scene: THREE.Scene;
    private labelsContainer: HTMLElement;
    private camera: THREE.PerspectiveCamera;
    private raycaster: THREE.Raycaster;
    private hoveredScreen: TVScreen | null = null;
    private crosshair: HTMLElement;
    private contentOverlay: ContentOverlay;
    private canvas: HTMLCanvasElement;
    private isOverlayOpen = false;

    // Screen configuration with unique colors
    private readonly screenConfigs: ScreenConfig[] = [
        { id: 'services', label: 'SERVICES', sublabel: 'What We Offer', color: new THREE.Color(0x7b2fff), route: '/services' },
        { id: 'about', label: 'ABOUT US', sublabel: 'Our Story & Mission', color: new THREE.Color(0x00d4ff), route: '/about' },
        { id: 'cases', label: 'CASE STUDY', sublabel: 'Featured Work', color: new THREE.Color(0xff2d7a), route: '/cases' },
        { id: 'tech', label: 'TECHNOLOGY', sublabel: 'Our Stack', color: new THREE.Color(0x00ff88), route: '/tech' },
        { id: 'team', label: 'OUR TEAM', sublabel: 'The Crew', color: new THREE.Color(0xffaa00), route: '/team' },
        { id: 'contact', label: 'CONTACT', sublabel: 'Get In Touch', color: new THREE.Color(0xff5555), route: '/contact' },
        { id: 'news', label: 'NEWS', sublabel: 'Latest Updates', color: new THREE.Color(0x55aaff), route: '/news' },
        { id: 'careers', label: 'CAREERS', sublabel: 'Join The Team', color: new THREE.Color(0xaa55ff), route: '/careers' },
    ];

    // Positioning
    private readonly radius = 7.5;
    private readonly screenHeight = 1.8;

    constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, canvas: HTMLCanvasElement) {
        this.scene = scene;
        this.camera = camera;
        this.canvas = canvas;
        this.raycaster = new THREE.Raycaster();
        this.labelsContainer = document.getElementById('screen-labels') as HTMLElement;
        this.crosshair = document.getElementById('crosshair') as HTMLElement;
        this.contentOverlay = new ContentOverlay();

        this.createScreens();
        this.setupInteraction();

        // Subscribe to language changes
        languageManager.subscribe(() => this.updateTexts());
    }

    private createScreens(): void {
        const angleStep = (Math.PI * 2) / this.screenConfigs.length;

        this.screenConfigs.forEach((config, index) => {
            const screen = new TVScreen(config);

            // Position in octagonal arrangement
            const angle = index * angleStep - Math.PI / 2; // Start from front
            const x = Math.sin(angle) * this.radius;
            const z = Math.cos(angle) * this.radius;

            screen.mesh.position.set(x, this.screenHeight, z);

            // Rotate to face center
            screen.mesh.rotation.y = angle + Math.PI;

            // Slight random tilt for visual interest
            screen.mesh.rotation.x = (Math.random() - 0.5) * 0.05;

            this.screens.push(screen);
            this.scene.add(screen.mesh);

            // Create label element
            this.createLabel(config);
        });
    }

    private createLabel(config: ScreenConfig): void {
        const label = document.createElement('div');
        label.className = 'screen-label';
        label.id = `label-${config.id}`;
        // Content will be populated by updateTexts
        this.labelsContainer.appendChild(label);
    }

    private updateTexts(): void {
        const ui = languageManager.getUI();

        this.screenConfigs.forEach(config => {
            const texts = ui.screens[config.id];
            if (!texts) return;

            const labelEl = document.getElementById(`label-${config.id}`);
            if (labelEl) {
                // Update config object in memory too so clicks have right label
                config.label = texts.label;
                config.sublabel = texts.sublabel;

                labelEl.innerHTML = `
                  <span class="label-icon">◆</span>
                  ${texts.label}
                  <span class="sublabel">${texts.sublabel}</span>
                `;
            }
        });
    }

    // Bound event handlers
    private boundOnPointerMove = this.onPointerMove.bind(this);
    private boundOnClick = this.onClick.bind(this);
    private boundOnTouchEnd = this.onTouchEnd.bind(this);

    private setupInteraction(): void {
        // Continuous checking for what's in view
        window.addEventListener('pointermove', this.boundOnPointerMove);
        
        // Use canvas for click events to avoid UI clicks propagating
        this.canvas.addEventListener('click', this.boundOnClick);
        this.canvas.addEventListener('touchend', this.boundOnTouchEnd);
    }

    private onPointerMove(_event: PointerEvent): void {
        if (!this.isOverlayOpen) {
            this.checkIntersections();
        }
    }

    private onTouchEnd(event: TouchEvent): void {
        if (!this.isOverlayOpen && event.changedTouches.length > 0) {
            this.handleClick();
        }
    }

    private checkIntersections(): void {
        // Check intersection with screens using center of view
        const centerVector = new THREE.Vector2(0, 0);
        this.raycaster.setFromCamera(centerVector, this.camera);

        const screenMeshes = this.screens.map(s => s.screenMesh);
        const intersects = this.raycaster.intersectObjects(screenMeshes, false);

        if (intersects.length > 0) {
            const screenId = intersects[0].object.userData.screenId;
            const screen = this.screens.find(s => s.config.id === screenId);

            if (screen && this.hoveredScreen !== screen) {
                // Unhover previous
                if (this.hoveredScreen) {
                    this.hoveredScreen.setHovered(false);
                    this.updateLabelVisibility(this.hoveredScreen.config.id, false);
                }

                // Hover new
                screen.setHovered(true);
                this.hoveredScreen = screen;
                this.updateLabelVisibility(screen.config.id, true);
                this.crosshair.classList.add('hovering');
            }
        } else if (this.hoveredScreen) {
            this.hoveredScreen.setHovered(false);
            this.updateLabelVisibility(this.hoveredScreen.config.id, false);
            this.hoveredScreen = null;
            this.crosshair.classList.remove('hovering');
        }
    }

    private onClick(): void {
        if (!this.isOverlayOpen) {
            this.handleClick();
        }
    }

    private handleClick(): void {
        if (this.hoveredScreen) {
            const screenId = this.hoveredScreen.config.id as 'about' | 'services' | 'cases' | 'tech' | 'team' | 'contact' | 'news' | 'careers';
            const label = this.hoveredScreen.config.label;
            console.log(`Opening content: ${label} (${screenId})`);

            // Visual feedback - pulse effect
            const screenMat = this.hoveredScreen.screenMesh.material as THREE.MeshStandardMaterial;
            const originalIntensity = screenMat.emissiveIntensity;
            screenMat.emissiveIntensity = 2.0;

            setTimeout(() => {
                screenMat.emissiveIntensity = originalIntensity;
            }, 200);

            // Open content overlay
            this.isOverlayOpen = true;
            this.contentOverlay.open(screenId, () => {
                this.isOverlayOpen = false;
            });
        }
    }

    private updateLabelVisibility(screenId: string, visible: boolean): void {
        const label = document.getElementById(`label-${screenId}`);
        if (label) {
            label.classList.toggle('visible', visible);
        }
    }

    public updateLabels(): void {
        if (this.isOverlayOpen) return;

        this.screens.forEach((screen) => {
            const label = document.getElementById(`label-${screen.config.id}`);
            if (!label) return;

            // Project screen position to screen space
            const screenPos = new THREE.Vector3();
            screen.mesh.getWorldPosition(screenPos);
            screenPos.y += 1.8; // Above the screen

            screenPos.project(this.camera);

            // Check if in front of camera
            const isBehind = screenPos.z > 1;

            if (!isBehind) {
                const x = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;

                label.style.left = `${x}px`;
                label.style.top = `${y}px`;
            }

            // Only show if hovered and in front
            if (screen.isHovered && !isBehind) {
                label.classList.add('visible');
            } else {
                label.classList.remove('visible');
            }
        });
    }

    public update(deltaTime: number): void {
        this.screens.forEach(screen => screen.update(deltaTime));
        this.updateLabels();
        if (!this.isOverlayOpen) {
            this.checkIntersections();
        }
    }

    public dispose(): void {
        // Remove event listeners
        window.removeEventListener('pointermove', this.boundOnPointerMove);
        this.canvas.removeEventListener('click', this.boundOnClick);
        this.canvas.removeEventListener('touchend', this.boundOnTouchEnd);

        this.screens.forEach(screen => {
            screen.dispose();
            this.scene.remove(screen.mesh);
        });
        this.labelsContainer.innerHTML = '';
    }
}
